import { getActiveCompany } from "src/Common/tenant";

// Accent applied to the PO number, section labels and the Total on the printed
// document. These belong to the paper document, not the app UI, so they are
// deliberately not theme tokens.
// `logoWidth` is the printed width in px; height follows the logo's own aspect
// ratio. The two brands' marks have different proportions, so they are sized
// individually rather than squeezed into one box.
export const BRANDS = {
  mrc: { key: "mrc", accent: "#2e7d32", accentDark: "#1f5723", logoWidth: 260 },
  rscs: { key: "rscs", accent: "#2f7cc4", accentDark: "#1c5691", logoWidth: 300 },
};

// A tenant that hasn't chosen an accent prints in neutral graphite. Not one of
// the two brand colours above: putting another company's blue on a new
// tenant's purchase orders is worse than printing something obviously plain.
const DEFAULT_ACCENT = "#3F4A5A";
const DEFAULT_LOGO_WIDTH = 280;

// The tenants that predate the per-branch setting, and whose documents must
// keep printing exactly as they do today until someone saves an accent for
// their branches. Delete this — and the name matching below — once they have.
const LEGACY_BRAND_COMPANIES = ["rscs", "mrc"];

const HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * The darker half of the pair, used for section labels and the far end of the
 * Total's gradient.
 *
 * Derived rather than asked for: picking a brand colour is a decision a tenant
 * can make, picking two shades that work together is a design task. 0.32 lands
 * within a couple of steps of both hand-picked pairs above, which keep their
 * exact values regardless.
 */
const darken = (hex, amount = 0.32) => {
  const channel = (start) => {
    const value = parseInt(hex.slice(start, start + 2), 16);
    return Math.max(0, Math.round(value * (1 - amount)))
      .toString(16)
      .padStart(2, "0");
  };
  return `#${channel(1)}${channel(3)}${channel(5)}`;
};

/** A branch's saved accent as a usable brand, or null if it has none. */
const savedBrand = (branch) => {
  const accent = String(branch?.documentAccent || "").trim();
  if (!HEX.test(accent)) return null;
  return { key: "custom", accent, accentDark: darken(accent), logoWidth: DEFAULT_LOGO_WIDTH };
};

/**
 * Which accent a PO prints in.
 *
 * The issuing **branch's** saved colour wins — one tenant runs branches under
 * more than one brand, so this cannot live on the company. Branches with
 * nothing saved fall back to the original name matching, but only for the two
 * tenants that shipped before the setting existed; everyone else gets the
 * neutral default rather than inheriting a stranger's brand.
 */
export const resolveBrand = (branch, company = getActiveCompany()) => {
  const saved = savedBrand(branch);
  if (saved) return saved;

  if (LEGACY_BRAND_COMPANIES.includes(String(company || "").toLowerCase())) {
    const haystack = `${branch?.name || ""} ${company || ""}`.toLowerCase();
    if (haystack.includes("mrc") || haystack.includes("mechanic")) return BRANDS.mrc;
    return BRANDS.rscs;
  }

  return {
    key: "default",
    accent: DEFAULT_ACCENT,
    accentDark: darken(DEFAULT_ACCENT),
    logoWidth: DEFAULT_LOGO_WIDTH,
  };
};
